import Image from 'next/image'
import { FC } from 'react'

interface CuideiLogoProps {
  /** `full` = symbol + "cuidei" wordmark (default); `symbol` = the icon mark only. */
  variant?: 'full' | 'symbol'
  size?: 'sm' | 'md' | 'lg'
}

/** Rendered height per size (px) — the width follows the artwork's aspect ratio. */
const FULL_HEIGHTS: Record<NonNullable<CuideiLogoProps['size']>, number> = { sm: 22, md: 28, lg: 40 }
const SYMBOL_HEIGHTS: Record<NonNullable<CuideiLogoProps['size']>, number> = { sm: 24, md: 32, lg: 48 }

/** Source artwork: the 512px lockup (also the JSON-LD logo) and the symbol, resized by next/image. */
const ARTWORK = {
  full: { src: '/cuidei/cuidei-logo-512.png', width: 512, height: 146 },
  symbol: { src: '/cuidei/cuidei-symbol.png', width: 1918, height: 1344 }
}

/**
 * Official Cuidei brand mark (real artwork in /public/cuidei).
 * - `full`: the horizontal lockup (two overlapping circles + plus, then "cuidei").
 * - `symbol`: just the icon, for tight/square spots.
 * Explicit width/height (no layout shift) and 1x/2x WebP from next/image instead of the 3632px PNG.
 */
const CuideiLogo: FC<CuideiLogoProps> = ({ variant = 'full', size = 'md' }) => {
  const artwork = ARTWORK[variant]
  const height = (variant === 'symbol' ? SYMBOL_HEIGHTS : FULL_HEIGHTS)[size]
  const width = Math.round((artwork.width / artwork.height) * height)

  return (
    <Image
      src={artwork.src}
      alt="Cuidei"
      width={width}
      height={height}
      loading="eager"
      draggable={false}
      style={{ userSelect: 'none', objectFit: 'contain' }}
    />
  )
}

export default CuideiLogo
